import {
  PythError,
  ErrorSeverity,
  PythErrorCode,
  type ErrorDetails,
} from "./base";

// WebSocket Error Codes
export enum WebsocketErrorCode {
  CONNECTION_FAILED = "CONNECTION_FAILED",
  CONNECTION_CLOSED = "CONNECTION_CLOSED",
  MESSAGE_PARSE_ERROR = "MESSAGE_PARSE_ERROR",
  SUBSCRIPTION_ERROR = "SUBSCRIPTION_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
}

// WebSocket States
export enum WebSocketState {
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  RECONNECTING = "RECONNECTING",
  DISCONNECTED = "DISCONNECTED",
  ERROR = "ERROR",
}

// WebSocket specific error class
export class WebsocketError extends PythError {
  public readonly name: string = "WebsocketError";

  constructor(
    code: WebsocketErrorCode | PythErrorCode,
    message: string,
    severity: ErrorSeverity = ErrorSeverity.HIGH,
    details?: ErrorDetails,
    originalError?: Error
  ) {
    super(code, message, severity, details, originalError);
    Object.setPrototypeOf(this, new.target.prototype);
  }

  // Factory methods
  static connectionTimeout(details?: ErrorDetails): WebsocketError {
    return new WebsocketError(
      PythErrorCode.WS_CONNECTION_TIMEOUT,
      "WebSocket connection timed out",
      ErrorSeverity.HIGH,
      details
    );
  }

  static connectionRefused(details?: ErrorDetails): WebsocketError {
    return new WebsocketError(
      PythErrorCode.WS_CONNECTION_REFUSED,
      "WebSocket connection refused",
      ErrorSeverity.HIGH,
      details
    );
  }

  static invalidUrl(url: string): WebsocketError {
    return new WebsocketError(
      PythErrorCode.WS_INVALID_URL,
      `Invalid WebSocket URL: ${url}`,
      ErrorSeverity.HIGH,
      { url }
    );
  }

  static sslError(details?: ErrorDetails): WebsocketError {
    return new WebsocketError(
      PythErrorCode.WS_SSL_ERROR,
      "SSL/TLS connection error",
      ErrorSeverity.HIGH,
      details
    );
  }

  static heartbeatFailure(details?: ErrorDetails): WebsocketError {
    return new WebsocketError(
      PythErrorCode.WS_HEARTBEAT_FAILED,
      "WebSocket heartbeat check failed",
      ErrorSeverity.HIGH,
      details
    );
  }

  static invalidStateTransition(
    fromState: WebSocketState,
    toState: WebSocketState,
    details?: ErrorDetails
  ): WebsocketError {
    return new WebsocketError(
      PythErrorCode.WS_INVALID_STATE_TRANSITION,
      `Invalid state transition from ${fromState} to ${toState}`,
      ErrorSeverity.HIGH,
      { fromState, toState, ...details }
    );
  }
}

